import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Search, Loader2, Trash2, ChevronDown, ChevronRight, Calendar, Target, Users, Layers, FileText, TrendingUp, ArrowUpRight, Shield, Crosshair, Globe, Zap, Printer, Download, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const intentColor = {
  informativo: 'bg-sky-100 text-sky-700',
  navigazionale: 'bg-purple-100 text-purple-700',
  commerciale: 'bg-amber-100 text-amber-700',
  transazionale: 'bg-emerald-100 text-emerald-700',
};

const priorityColor = {
  alta: 'bg-red-100 text-red-700',
  media: 'bg-amber-100 text-amber-700',
  bassa: 'bg-slate-100 text-slate-600',
};

const formatLabel = {
  articolo: 'Articolo', guida: 'Guida', lista: 'Lista', intervista: 'Intervista', 'caso studio': 'Caso Studio',
};

const fmtNum = (n) => n != null ? new Intl.NumberFormat("it-IT").format(n) : "-";

const DifficultyBar = ({ value }) => {
  const color = value < 30 ? 'bg-emerald-500' : value < 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-12 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }}></div>
      </div>
      <span className="text-[10px] tabular-nums">{value}</span>
    </div>
  );
};

// Keyword Table Tab
const KeywordTab = ({ keywords }) => {
  const [sortBy, setSortBy] = useState('volume');
  const [filterIntent, setFilterIntent] = useState('all');

  const filtered = (keywords || [])
    .filter(k => filterIntent === 'all' || k.intent === filterIntent)
    .sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));

  return (
    <div className="space-y-3" data-testid="keyword-tab">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs font-medium text-slate-500">Filtra per intento:</span>
        {['all', 'informativo', 'navigazionale', 'commerciale', 'transazionale'].map(intent => (
          <button key={intent} onClick={() => setFilterIntent(intent)}
            className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${
              filterIntent === intent ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
            }`}>
            {intent === 'all' ? `Tutti (${keywords?.length || 0})` : intent.charAt(0).toUpperCase() + intent.slice(1)}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-400">{filtered.length} keyword</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-xs" data-testid="keyword-table">
          <thead>
            <tr className="bg-slate-50 border-b">
              <th className="text-left px-3 py-2 font-semibold">Keyword</th>
              <th className="text-right px-3 py-2 font-semibold cursor-pointer hover:text-blue-600" onClick={() => setSortBy('volume')}>
                Volume {sortBy === 'volume' && <ArrowUpRight className="h-3 w-3 inline" />}
              </th>
              <th className="text-right px-3 py-2 font-semibold cursor-pointer hover:text-blue-600" onClick={() => setSortBy('cpc')}>
                CPC {sortBy === 'cpc' && <ArrowUpRight className="h-3 w-3 inline" />}
              </th>
              <th className="text-center px-3 py-2 font-semibold cursor-pointer hover:text-blue-600" onClick={() => setSortBy('competition')}>
                Competizione {sortBy === 'competition' && <ArrowUpRight className="h-3 w-3 inline" />}
              </th>
              <th className="text-center px-3 py-2 font-semibold cursor-pointer hover:text-blue-600" onClick={() => setSortBy('difficulty')}>
                Difficolta {sortBy === 'difficulty' && <ArrowUpRight className="h-3 w-3 inline" />}
              </th>
              <th className="text-center px-3 py-2 font-semibold">Intento</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((kw, i) => (
              <tr key={i} className="border-b hover:bg-slate-50 transition-colors">
                <td className="px-3 py-1.5 font-medium">{kw.keyword}</td>
                <td className="text-right px-3 py-1.5 tabular-nums font-semibold">{fmtNum(kw.volume)}</td>
                <td className="text-right px-3 py-1.5 tabular-nums text-slate-600">{kw.cpc != null ? `${kw.cpc.toFixed(2)}` : '-'}</td>
                <td className="text-center px-3 py-1.5"><DifficultyBar value={kw.competition || 0} /></td>
                <td className="text-center px-3 py-1.5"><DifficultyBar value={kw.difficulty || 0} /></td>
                <td className="text-center px-3 py-1.5">
                  <Badge className={`text-[9px] ${intentColor[kw.intent] || 'bg-slate-100 text-slate-500'}`}>{kw.intent}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Strategy Tab (Questions + Clusters + Editorial Plan)
const StrategyTab = ({ results }) => {
  const [openCluster, setOpenCluster] = useState(null);
  const months = ['Mese 1', 'Mese 2', 'Mese 3'];

  return (
    <div className="space-y-4" data-testid="strategy-tab">
      {/* Questions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4 text-sky-600" /> Domande del Pubblico</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(results.questions || []).map((q, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-sky-50 rounded-lg border border-sky-100">
                <Search className="h-3.5 w-3.5 text-sky-500 mt-0.5 flex-shrink-0" />
                <span className="text-xs">{q}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Clusters */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Layers className="h-4 w-4 text-violet-600" /> Cluster Tematici</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          {(results.clusters || []).map((cl, i) => (
            <div key={i} className="border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                   onClick={() => setOpenCluster(openCluster === i ? null : i)}>
                <div className="flex items-center gap-2">
                  {openCluster === i ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  <span className="text-xs font-semibold">{cl.name}</span>
                  <Badge variant="outline" className="text-[9px]">{cl.keywords?.length || 0} kw</Badge>
                </div>
              </div>
              {openCluster === i && (
                <div className="px-3 py-2 border-t">
                  <p className="text-[11px] text-slate-500 mb-2">{cl.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {(cl.keywords || []).map((kw, ki) => (
                      <Badge key={ki} variant="secondary" className="text-[9px]">{kw}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Editorial Plan */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Calendar className="h-4 w-4 text-emerald-600" /> Piano Editoriale (3 mesi)</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(results.editorial_plan || []).map((plan, mi) => (
              <div key={mi} className="border rounded-lg overflow-hidden">
                <div className="bg-slate-800 text-white px-3 py-1.5 text-xs font-bold">{months[mi] || `Mese ${plan.month}`}</div>
                <div className="space-y-1.5 p-2">
                  {(plan.articles || []).map((art, ai) => (
                    <div key={ai} className="p-2 bg-slate-50 rounded border text-[11px]">
                      <div className="font-semibold mb-1 leading-tight">{art.title}</div>
                      <div className="flex items-center gap-1.5">
                        <Badge className={`text-[8px] py-0 ${priorityColor[art.priority] || ''}`}>{art.priority}</Badge>
                        <Badge variant="outline" className="text-[8px] py-0">{formatLabel[art.format] || art.format}</Badge>
                      </div>
                      {art.target_keywords?.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mt-1">
                          {art.target_keywords.slice(0, 3).map((kw, ki) => (
                            <span key={ki} className="text-[8px] text-blue-500">#{kw.replace(/\s+/g, '_')}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


// Competitor Tab
const CompetitorTab = ({ results }) => {
  const posColor = {
    leader: 'bg-red-100 text-red-700 border-red-200',
    sfidante: 'bg-amber-100 text-amber-700 border-amber-200',
    nicchia: 'bg-blue-100 text-blue-700 border-blue-200',
  };

  return (
    <div className="space-y-4" data-testid="competitor-tab">
      {/* Verified Source Disclaimer */}
      <div className={`rounded-lg p-3 text-[11px] flex items-start gap-2 ${results.competitors_search_used ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`} data-testid="competitor-source-disclaimer">
        <Globe className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          {results.competitors_search_used ? (
            <>
              <strong>Competitor verificati sul web</strong> via DuckDuckGo (
              {results.competitors_local_count > 0 ? `${results.competitors_local_count} locali` : 'nessun locale'}
              {' + '}
              {results.competitors_national_count > 0 ? `${results.competitors_national_count} nazionali` : 'nessun nazionale'}
              ). Nomi e siti web sono <strong>reali</strong>. L'analisi di posizionamento, punti di forza e debolezze è una stima AI basata sulle informazioni pubblicate dai competitor stessi.
              {results.competitors_local_count === 0 && (
                <span className="block mt-1 text-amber-700">⚠️ Nessun competitor locale trovato — verifica che la <strong>città</strong> sia compilata nella scheda anagrafica del cliente.</span>
              )}
            </>
          ) : (
            <>
              <strong>Attenzione:</strong> ricerca web fallita. Imposta la città nella scheda anagrafica del cliente e rilancia l'analisi per ottenere competitor reali.
            </>
          )}
        </div>
      </div>

      {/* Competitors */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Shield className="h-4 w-4 text-red-600" /> Competitor Principali</CardTitle>
          <p className="text-[11px] text-muted-foreground">Aziende che dominano le ricerche Google in questo settore</p>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(results.competitors || []).map((comp, i) => (
              <div key={i} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b">
                  <div className="flex items-center gap-2">
                    <Globe className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-xs font-bold">{comp.name}</span>
                    {comp.type && (
                      <Badge variant="outline" className={`text-[8px] ${comp.type === 'locale' ? 'border-blue-300 text-blue-600' : 'border-slate-300 text-slate-500'}`}>
                        {comp.type === 'locale' ? 'Locale' : 'Nazionale'}
                      </Badge>
                    )}
                    {comp.verified && (
                      <Badge className="text-[8px] bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`competitor-verified-${i}`}>
                        ✓ Verificato
                      </Badge>
                    )}
                  </div>
                  <Badge className={`text-[9px] ${posColor[comp.positioning] || posColor.nicchia}`}>
                    {comp.positioning}
                  </Badge>
                </div>
                <div className="p-3 space-y-2">
                  {comp.website && (
                    <a
                      href={comp.website.startsWith('http') ? comp.website : `https://${comp.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-blue-600 hover:text-blue-800 hover:underline truncate block"
                    >
                      {comp.website}
                    </a>
                  )}
                  <div>
                    <p className="text-[9px] font-semibold text-slate-500 mb-1">Punti di forza</p>
                    <div className="flex flex-wrap gap-1">
                      {(comp.strengths || []).map((s, si) => (
                        <span key={si} className="text-[9px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-100">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[9px] font-semibold text-slate-500 mb-1">Keyword principali</p>
                    <div className="flex flex-wrap gap-1">
                      {(comp.main_keywords || []).map((kw, ki) => (
                        <Badge key={ki} variant="secondary" className="text-[8px]">{kw}</Badge>
                      ))}
                    </div>
                  </div>
                  {comp.weakness && (
                    <div className="mt-1 p-1.5 bg-amber-50 rounded border border-amber-100">
                      <p className="text-[9px] text-amber-700"><Crosshair className="h-2.5 w-2.5 inline mr-1" /><strong>Punto debole:</strong> {comp.weakness}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Opportunities */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /> Opportunita di Mercato</CardTitle>
          <p className="text-[11px] text-muted-foreground">Aree dove i competitor sono deboli e il tuo cliente puo inserirsi</p>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            {(results.opportunities || []).map((opp, i) => (
              <div key={i} className={`p-3 rounded-lg border-l-4 ${
                opp.priority === 'alta' ? 'border-l-red-500 bg-red-50/50' :
                opp.priority === 'media' ? 'border-l-amber-500 bg-amber-50/50' :
                'border-l-slate-300 bg-slate-50/50'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold">{opp.keyword}</span>
                  <Badge className={`text-[8px] ${
                    opp.priority === 'alta' ? 'bg-red-100 text-red-700' :
                    opp.priority === 'media' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>{opp.priority}</Badge>
                </div>
                <p className="text-[11px] text-slate-600 mb-1">{opp.reason}</p>
                <div className="flex items-start gap-1.5 mt-1.5 p-1.5 bg-white rounded border">
                  <Target className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] text-blue-700 font-medium">{opp.action}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


export const MarketResearchPage = ({ clientId, clientName }) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [activeResearch, setActiveResearch] = useState(null);
  const [activeTab, setActiveTab] = useState('keywords');
  const [clientProducts, setClientProducts] = useState([]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/market-research`);
      setHistory(res.data);
      if (res.data.length > 0 && !activeResearch) {
        setActiveResearch(res.data[0]);
      }
    } catch {}
  }, [clientId]);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/products`);
      setClientProducts((res.data?.products || []).map(p => p.name).filter(Boolean));
    } catch {}
  }, [clientId]);

  useEffect(() => { fetchHistory(); fetchProducts(); }, [fetchHistory, fetchProducts]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/market-research`, { query: query.trim() });
      const newResearch = { id: res.data.id, query: res.data.query, status: 'pending', results: {}, created_at: new Date().toISOString() };
      setActiveResearch(newResearch);
      setActiveTab('keywords');
      toast.success(`Raccolta keyword completata (${res.data.google_keywords_count} da Google). Analisi AI in corso...`);
      setQuery('');
      // Start polling for results
      pollForResults(res.data.id);
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
      setLoading(false);
    }
  };

  const pollForResults = (researchId) => {
    let attempts = 0;
    const maxAttempts = 40; // 40 x 5s = 200s max
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await axios.get(`${API}/clients/${clientId}/market-research`);
        const found = res.data.find(r => r.id === researchId);
        if (found && found.status === 'done') {
          clearInterval(interval);
          setActiveResearch(found);
          setHistory(res.data);
          setLoading(false);
          toast.success(`Analisi completata: ${found.results?.keywords?.length || 0} keyword analizzate!`);
        } else if (found && found.status === 'error') {
          clearInterval(interval);
          setActiveResearch(found);
          setHistory(res.data);
          setLoading(false);
          toast.error("Errore nell'analisi AI: " + (found.results?.error || 'sconosciuto'));
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setLoading(false);
          toast.error("Timeout: l'analisi sta impiegando troppo tempo. Ricarica la pagina per controllare.");
          fetchHistory();
        }
      } catch {
        // Ignore polling errors
      }
    }, 5000);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/clients/${clientId}/market-research/${id}`);
      if (activeResearch?.id === id) setActiveResearch(null);
      fetchHistory();
      toast.success("Ricerca eliminata");
    } catch { toast.error("Errore"); }
  };

  const handleRetry = async (id) => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/clients/${clientId}/market-research/${id}/retry`);
      const retried = { id: res.data.id, query: res.data.query, status: 'pending', results: {}, created_at: new Date().toISOString() };
      setActiveResearch(retried);
      toast.success(`Rianalisi avviata (${res.data.google_keywords_count} keyword da Google). Analisi AI in corso...`);
      pollForResults(res.data.id);
    } catch (e) {
      toast.error("Errore nel retry: " + (e.response?.data?.detail || e.message));
      setLoading(false);
    }
  };

  const results = activeResearch?.results || {};
  const kwCount = results.keywords?.length || 0;
  const qCount = results.questions?.length || 0;
  const clCount = results.clusters?.length || 0;
  const compCount = results.competitors?.length || 0;
  const oppCount = results.opportunities?.length || 0;

  const [editorialHtml, setEditorialHtml] = useState(null);

  const handlePrint = () => {
    window.print();
  };

  const handlePrintEditorial = () => {
    if (!results.editorial_plan || results.editorial_plan.length === 0) {
      toast.error("Nessun piano editoriale disponibile per la stampa");
      return;
    }
    const months = ['Mese 1', 'Mese 2', 'Mese 3'];
    const today = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
    const logoUrl = `${window.location.origin}/sc-icon-192.png`;

    const priorityStyles = {
      alta: { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b', label: 'PRIORITÀ ALTA' },
      media: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e', label: 'PRIORITÀ MEDIA' },
      bassa: { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569', label: 'PRIORITÀ BASSA' },
    };
    const formatLabels = { articolo: 'Articolo', guida: 'Guida', lista: 'Lista', intervista: 'Intervista', 'caso studio': 'Caso Studio' };

    const renderArticle = (art) => {
      const p = priorityStyles[art.priority] || priorityStyles.bassa;
      const fmt = formatLabels[art.format] || art.format || 'Articolo';
      const kws = (art.target_keywords || []).slice(0, 4)
        .map(k => `<span class="kw">#${(k || '').replace(/\s+/g, '_')}</span>`).join(' ');
      return `
        <div class="article" style="border-left:4px solid ${p.border};background:${p.bg}">
          <div class="prio" style="color:${p.text}">${p.label} · ${fmt}</div>
          <div class="title">${(art.title || '').replace(/</g, '&lt;')}</div>
          ${kws ? `<div class="kws">${kws}</div>` : ''}
          ${art.angle ? `<div class="angle">💡 ${(art.angle || '').replace(/</g, '&lt;')}</div>` : ''}
        </div>`;
    };

    const monthsHtml = (results.editorial_plan || []).map((plan, mi) => {
      const articles = (plan.articles || []).map(renderArticle).join('');
      const monthLabel = months[mi] || `Mese ${plan.month || mi + 1}`;
      return `
        <section class="month">
          <div class="month-head">
            <div class="month-num">${mi + 1}</div>
            <div>
              <div class="month-label">${monthLabel}</div>
              ${plan.theme ? `<div class="month-theme">${(plan.theme || '').replace(/</g, '&lt;')}</div>` : ''}
            </div>
          </div>
          <div class="articles">${articles}</div>
        </section>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="it">
<head>
<meta charset="utf-8">
<title>Piano Editoriale - ${(clientName || '').replace(/</g, '&lt;')}</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; color: #0f172a; background: #fff; }
  .page { max-width: 800px; margin: 0 auto; padding: 32px 28px 24px; }
  .header { display: flex; align-items: center; gap: 16px; padding-bottom: 18px; border-bottom: 3px solid #0f172a; margin-bottom: 24px; }
  .logo { width: 56px; height: 56px; border-radius: 8px; flex-shrink: 0; }
  .brand { flex: 1; }
  .brand-name { font-size: 18px; font-weight: 800; letter-spacing: 0.5px; }
  .brand-sub { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }
  .doc-meta { text-align: right; font-size: 11px; color: #64748b; }
  .doc-meta strong { display:block; color:#0f172a; font-size: 12px; margin-bottom: 2px; }
  h1 { font-size: 28px; font-weight: 900; margin: 0 0 4px 0; line-height: 1.15; }
  .subtitle { font-size: 13px; color: #475569; margin-bottom: 12px; }
  .for-client { background: linear-gradient(135deg,#0f172a,#1e293b); color: #fff; padding: 14px 18px; border-radius: 10px; margin: 18px 0 24px; }
  .for-client .label { font-size: 10px; text-transform: uppercase; letter-spacing: 2px; opacity: 0.7; }
  .for-client .name { font-size: 22px; font-weight: 800; margin-top: 4px; }
  .for-client .topic { font-size: 12px; opacity: 0.85; margin-top: 6px; }
  .legend { display:flex; gap: 12px; flex-wrap: wrap; margin-bottom: 18px; font-size: 10px; }
  .legend span { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; font-weight: 600; }
  .month { margin-bottom: 22px; page-break-inside: avoid; }
  .month-head { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
  .month-num { width: 36px; height: 36px; background: #0f172a; color: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; }
  .month-label { font-size: 18px; font-weight: 800; }
  .month-theme { font-size: 11px; color: #64748b; font-style: italic; }
  .articles { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .article { padding: 10px 12px; border-radius: 6px; }
  .article .prio { font-size: 9px; font-weight: 800; letter-spacing: 0.8px; margin-bottom: 4px; }
  .article .title { font-size: 13px; font-weight: 700; line-height: 1.3; margin-bottom: 6px; color: #0f172a; }
  .article .kws { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
  .article .kw { font-size: 9px; color: #2563eb; background: #fff; padding: 1px 6px; border-radius: 3px; border: 1px solid #dbeafe; }
  .article .angle { font-size: 10px; color: #475569; margin-top: 6px; padding-top: 6px; border-top: 1px dashed #cbd5e1; line-height: 1.4; }
  .footer { margin-top: 32px; padding-top: 14px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; font-size: 10px; color: #64748b; }
  @media print {
    @page { margin: 15mm 10mm; size: A4; }
    .page { padding: 0; }
    .month { page-break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="page">
    <header class="header">
      <img src="${logoUrl}" class="logo" alt="SC Logo" onerror="this.style.display='none'">
      <div class="brand">
        <div class="brand-name">SC · Casali Silvia</div>
        <div class="brand-sub">Dottori Commercialisti · Controllo di Gestione</div>
      </div>
      <div class="doc-meta">
        <strong>Piano Editoriale</strong>
        ${today}
      </div>
    </header>

    <h1>Piano Editoriale Marketing</h1>
    <div class="subtitle">3 mesi di contenuti suggeriti per la tua presenza online</div>

    <div class="for-client">
      <div class="label">Preparato per</div>
      <div class="name">${(clientName || 'Cliente').replace(/</g, '&lt;')}</div>
      <div class="topic">Argomento di analisi: <strong>${(activeResearch?.query || '').replace(/</g, '&lt;')}</strong></div>
    </div>

    <div class="legend">
      <span style="background:#fee2e2;color:#991b1b">🔴 Priorità Alta · pubblica per primo</span>
      <span style="background:#fef3c7;color:#92400e">🟡 Priorità Media</span>
      <span style="background:#f1f5f9;color:#475569">⚪ Priorità Bassa</span>
    </div>

    ${monthsHtml}

    <footer class="footer">
      <div>SC · Casali Silvia · Dottori Commercialisti</div>
      <div>Documento generato il ${today}</div>
    </footer>
  </div>
</body>
</html>`;

    setEditorialHtml(html);
  };

  const handlePrintIframe = () => {
    const iframe = document.getElementById('editorial-print-iframe');
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  };

  const handleDownloadHtml = () => {
    if (!editorialHtml) return;
    const blob = new Blob([editorialHtml], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = (clientName || 'cliente').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
    a.download = `piano-editoriale-${safeName}-${new Date().toISOString().slice(0, 10)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="market-research-page">
      {/* Header */}
      <Card className="no-print">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="h-4 w-4 text-blue-600" /> Cosa cerca il tuo cliente
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Inserisci il servizio o prodotto del cliente per scoprire cosa cercano i suoi potenziali clienti su Google.
            Ottieni keyword reali, domande del pubblico, cluster tematici e un piano editoriale di 3 mesi.
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex gap-2">
            <Input
              data-testid="market-search-input"
              placeholder="Es: serramenti in PVC, ristorante sushi, dentista implantologia..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !loading && handleSearch()}
              disabled={loading}
              className="flex-1"
            />
            <Button onClick={handleSearch} disabled={loading || !query.trim()} data-testid="market-search-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
              {loading ? 'Analisi...' : 'Analizza'}
            </Button>
          </div>
          {loading && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100 text-xs text-blue-700 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Raccolta keyword da Google e analisi AI in corso... (circa 30 secondi)
            </div>
          )}
          {clientProducts.length > 0 && !loading && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-muted-foreground font-medium">Servizi del cliente:</span>
              {clientProducts.map(p => (
                <button key={p} onClick={() => { setQuery(p); }}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-800 hover:text-white text-slate-600 transition-colors cursor-pointer border"
                  data-testid={`product-suggest-${p.toLowerCase().replace(/\s+/g, '-')}`}>
                  {p}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* History sidebar + Results */}
      <div className="grid grid-cols-12 gap-4 print:block">
        {/* History */}
        <div className="col-span-3 no-print">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-semibold text-slate-500">Ricerche salvate</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-1.5 max-h-[500px] overflow-y-auto">
              {history.length === 0 && <p className="text-[11px] text-slate-400 italic">Nessuna ricerca</p>}
              {history.map(h => (
                <div key={h.id}
                  className={`flex items-center justify-between p-2 rounded border cursor-pointer transition-colors ${
                    activeResearch?.id === h.id ? 'bg-blue-50 border-blue-200' : 'hover:bg-slate-50 border-slate-100'
                  }`}
                  onClick={() => { setActiveResearch(h); setActiveTab('keywords'); }}>
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium truncate flex items-center gap-1">
                      {h.query}
                      {h.status === 'pending' && <Loader2 className="h-2.5 w-2.5 animate-spin text-blue-500" />}
                    </p>
                    <p className="text-[9px] text-slate-400">
                      {new Date(h.created_at).toLocaleDateString('it-IT')}
                      {h.status === 'done' ? ` - ${h.results?.keywords?.length || 0} kw` : h.status === 'pending' ? ' - in corso...' : ' - errore ⚠'}
                    </p>
                  </div>
                  {h.status === 'error' && (
                    <button onClick={e => { e.stopPropagation(); handleRetry(h.id); }} className="text-blue-400 hover:text-blue-600 transition-colors p-0.5 mr-1" title="Riprova" data-testid={`retry-research-${h.id}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                    </button>
                  )}
                  <button onClick={e => { e.stopPropagation(); handleDelete(h.id); }} className="text-slate-300 hover:text-red-500 transition-colors p-0.5">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Results */}
        <div className="col-span-9 print:col-span-12">
          {!activeResearch ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Inserisci un servizio o prodotto per iniziare l'analisi</p>
              </CardContent>
            </Card>
          ) : activeResearch.status === 'pending' ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Loader2 className="h-8 w-8 text-blue-500 mx-auto mb-3 animate-spin" />
                <p className="text-sm font-semibold text-blue-700">Analisi in corso per "{activeResearch.query}"</p>
                <p className="text-xs text-slate-500 mt-1">Stiamo raccogliendo keyword da Google e generando l'analisi AI...</p>
                <p className="text-xs text-slate-400 mt-1">Circa 30-60 secondi. La pagina si aggiornera automaticamente.</p>
              </CardContent>
            </Card>
          ) : activeResearch.status === 'error' ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Shield className="h-8 w-8 text-red-400 mx-auto mb-3" />
                <p className="text-sm font-semibold text-red-700">Analisi non riuscita per "{activeResearch.query}"</p>
                <p className="text-xs text-slate-500 mt-2">{activeResearch.results?.error || "Si è verificato un errore durante l'analisi AI."}</p>
                <Button
                  onClick={() => handleRetry(activeResearch.id)}
                  disabled={loading}
                  className="mt-4"
                  variant="outline"
                  size="sm"
                  data-testid="retry-research-main"
                >
                  {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>}
                  Riprova analisi
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {/* Tab bar */}
              <div className="flex items-center gap-2 border-b pb-2 no-print">
                <h3 className="text-sm font-semibold mr-3">"{activeResearch.query}"</h3>
                <button onClick={() => setActiveTab('keywords')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1 ${
                    activeTab === 'keywords' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'
                  }`} data-testid="tab-keywords">
                  <TrendingUp className="h-3 w-3" /> Keyword ({kwCount})
                </button>
                <button onClick={() => setActiveTab('strategy')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1 ${
                    activeTab === 'strategy' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'
                  }`} data-testid="tab-strategy">
                  <FileText className="h-3 w-3" /> Strategia ({qCount + clCount})
                </button>
                <button onClick={() => setActiveTab('competitor')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1 ${
                    activeTab === 'competitor' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'
                  }`} data-testid="tab-competitor">
                  <Shield className="h-3 w-3" /> Competitor ({compCount})
                </button>
                <Button variant="outline" size="sm" className="ml-auto h-7 text-xs" onClick={handlePrint} data-testid="print-research-btn">
                  <Printer className="h-3 w-3 mr-1" /> Stampa
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                  onClick={handlePrintEditorial}
                  disabled={!results.editorial_plan?.length}
                  data-testid="print-editorial-plan-btn"
                  title="Stampa il piano editoriale brandizzato per il cliente"
                >
                  <Printer className="h-3 w-3 mr-1" /> Piano per cliente
                </Button>
              </div>

              {/* KPI Summary */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 no-print">
                <div className="bg-blue-50 rounded-lg p-2 border border-blue-100 text-center">
                  <div className="text-lg font-bold text-blue-700">{kwCount}</div>
                  <div className="text-[10px] text-blue-500">Keyword</div>
                </div>
                <div className="bg-sky-50 rounded-lg p-2 border border-sky-100 text-center">
                  <div className="text-lg font-bold text-sky-700">{qCount}</div>
                  <div className="text-[10px] text-sky-500">Domande</div>
                </div>
                <div className="bg-violet-50 rounded-lg p-2 border border-violet-100 text-center">
                  <div className="text-lg font-bold text-violet-700">{clCount}</div>
                  <div className="text-[10px] text-violet-500">Cluster</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-100 text-center">
                  <div className="text-lg font-bold text-emerald-700">
                    {results.editorial_plan?.reduce((acc, p) => acc + (p.articles?.length || 0), 0) || 0}
                  </div>
                  <div className="text-[10px] text-emerald-500">Articoli</div>
                </div>
                <div className="bg-red-50 rounded-lg p-2 border border-red-100 text-center">
                  <div className="text-lg font-bold text-red-700">{compCount}</div>
                  <div className="text-[10px] text-red-500">Competitor</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-2 border border-amber-100 text-center">
                  <div className="text-lg font-bold text-amber-700">{oppCount}</div>
                  <div className="text-[10px] text-amber-500">Opportunita</div>
                </div>
              </div>

              {/* Active Tab Content */}
              {activeTab === 'keywords' && <KeywordTab keywords={results.keywords} />}
              {activeTab === 'strategy' && <StrategyTab results={results} />}
              {activeTab === 'competitor' && <CompetitorTab results={results} />}
            </div>
          )}
        </div>
      </div>

      {/* Editorial Plan Print Modal */}
      {editorialHtml && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex flex-col" data-testid="editorial-print-modal">
          <div className="bg-white border-b shadow-sm px-4 py-3 flex items-center gap-3">
            <Printer className="h-4 w-4 text-emerald-600" />
            <div className="font-semibold text-sm">Anteprima piano editoriale per <span className="text-emerald-700">{clientName}</span></div>
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={handleDownloadHtml} data-testid="download-editorial-html-btn">
                <Download className="h-3.5 w-3.5 mr-1" /> Scarica HTML
              </Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handlePrintIframe} data-testid="confirm-print-editorial-btn">
                <Printer className="h-3.5 w-3.5 mr-1" /> Stampa / Salva PDF
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditorialHtml(null)} data-testid="close-editorial-modal-btn">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden bg-slate-100 p-4">
            <iframe
              id="editorial-print-iframe"
              title="Piano editoriale per il cliente"
              srcDoc={editorialHtml}
              className="w-full h-full bg-white rounded shadow-lg border-0"
            />
          </div>
        </div>
      )}
    </div>
  );
};
